import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import MiniDoc from './MiniDoc.jsx';

// One size for both action icons (upload + camera) so they read as one consistent, same-weight set.
const ICON_SIZE = 24;

const DocumentSlot = ({
	side, label, status = 'empty', thumb, thumbIsPdf = false, text, disabled,
	miniDocVariant = 'card', band = false,
	cameraEnabled = true, cameraPrimary = true, forceCamera = false,
	onCamera, onFile,
}) => {
	// Available inputs, device-default first. Camera dropped when useCamera is off; file dropped on a kiosk.
	const fileInput = { testid: `slot-file-${side}`, Icon: PhotoLibraryOutlinedIcon, fn: onFile, label: text.slotUploadMain };
	const camInput = { testid: `slot-camera-${side}`, Icon: PhotoCameraOutlinedIcon, fn: onCamera, label: text.slotCameraOpen };
	const cameraOnly = cameraEnabled && forceCamera;   // registered kiosk → camera only, no file system
	const inputs = !cameraEnabled ? [fileInput]
		: cameraOnly ? [camInput]
			: (cameraPrimary ? [camInput, fileInput] : [fileInput, camInput]);

	// Filled/flagged: a neutral capture-status row (thumbnail + label + "tap to retake"). States only "I
	// have this image", never a verdict — no ✓/⚠, neutral border. `flagged` only tweaks the caption.
	if (status === 'done' || status === 'flagged') {
		const flagged = status === 'flagged';
		const retake = inputs[0];
		return (
			<Card data-testid={`slot-${side}`} variant="outlined" sx={{ mb: 1.5, borderStyle: 'solid', borderWidth: 1.5, borderColor: 'divider' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', minHeight: 72 }}>
					<CardActionArea onClick={retake.fn} disabled={disabled} aria-label={retake.label}
						sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, flex: 1, minWidth: 0 }}>
						<Box sx={{ width: 46, height: 32, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: 'action.hover',
							display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							{/* PDFs can't render in <img>, so show the PDF glyph; images keep their real thumbnail. */}
							{thumbIsPdf
								? <PictureAsPdfOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
								: (thumb && <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />)}
						</Box>
						<Box sx={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
							<Typography sx={{ fontWeight: 600 }}>{label}</Typography>
							<Typography variant="caption" color={flagged ? 'warning.main' : 'text.secondary'}>
								{flagged ? text.slotFlaggedBlur : text.slotCaptured}
							</Typography>
						</Box>
					</CardActionArea>
					<Box sx={{ display: 'flex', flexShrink: 0, pr: 0.5 }}>
						{inputs.map((r) => (
							<IconButton key={r.testid} data-testid={r.testid} onClick={r.fn} disabled={disabled} aria-label={r.label} sx={{ color: 'text.secondary' }}>
								<r.Icon />
							</IconButton>
						))}
					</Box>
				</Box>
			</Card>
		);
	}

	// Uploading: non-interactive progress row.
	if (status === 'uploading') {
		return (
			<Card data-testid={`slot-${side}`} variant="outlined" sx={{ mb: 1.5, p: 1.5, display: 'flex', alignItems: 'center', gap: 2, minHeight: 72 }}>
				<Box sx={{ color: 'text.disabled', display: 'flex' }}><MiniDoc variant={miniDocVariant} band={band} face={side} size={24} /></Box>
				<Box sx={{ flex: 1, textAlign: 'left' }}>
					<Typography sx={{ fontWeight: 600 }}>{label}</Typography>
					<Typography variant="caption" color="text.secondary">{text.feedbackReview}</Typography>
				</Box>
				<CircularProgress size={20} />
			</Card>
		);
	}

	// Empty: 70/30 row — device-default action fills the 70% card, the alternate the compact 30% card.
	// Kiosk = camera only; useCamera=false = upload only. Never stacked.
	const primary = inputs[0];
	const secondary = inputs[1] || null;

	const primaryCard = (
		<Card key="primary" variant="outlined" sx={{ borderStyle: 'dashed', borderWidth: 1.5, flex: secondary ? '80 1 0' : '1 1 auto', minWidth: 0 }}>
			<CardActionArea data-testid={primary.testid} onClick={primary.fn} disabled={disabled}
				sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, minHeight: 72, height: '100%' }}>
				<Box sx={{ color: 'text.primary', display: 'flex', flexShrink: 0 }}>
					<MiniDoc variant={miniDocVariant} band={band} face={side} size={24} />
				</Box>
				<Box sx={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
					<Typography sx={{ fontWeight: 600 }}>{label}</Typography>
					<Typography variant="caption" color="text.secondary">{primary.label}</Typography>
				</Box>
				<Box sx={{ color: 'text.primary', display: 'flex', flexShrink: 0 }}><primary.Icon sx={{ fontSize: ICON_SIZE }} /></Box>
			</CardActionArea>
		</Card>
	);

	// Secondary (alternate) card: icon only, centered; aria-label carries the accessible name.
	const secondaryCard = secondary && (
		<Card key="secondary" variant="outlined" sx={{ borderStyle: 'dashed', borderWidth: 1.5, flex: '20 1 0', minWidth: 0 }}>
			<CardActionArea data-testid={secondary.testid} onClick={secondary.fn} disabled={disabled} aria-label={secondary.label}
				sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1.5, minHeight: 72, height: '100%' }}>
				<Box sx={{ color: 'text.primary', display: 'flex' }}><secondary.Icon sx={{ fontSize: ICON_SIZE }} /></Box>
			</CardActionArea>
		</Card>
	);

	const orLabel = secondary && (
		<Typography key="or" variant="caption" color="text.secondary"
			sx={{ flexShrink: 0, alignSelf: 'center', textTransform: 'uppercase', letterSpacing: 0.5 }}>
			{text.orLabel}
		</Typography>
	);

	return (
		<Box data-testid={`slot-${side}`} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 1.5, mb: 1.5 }}>
			{primaryCard}
			{orLabel}
			{secondaryCard}
		</Box>
	);
};

export default DocumentSlot;
